import React, { useEffect, useRef, useState } from "react";
import { Check, Search, Loader2, Package, ChevronRight } from "lucide-react";

import useDocumentList from "../../hooks/useDocumentList";
import ItemListDrawerSalesReturn from "./ItemListDrawerSalesReturn";

const TransferInvoiceListModal = ({
  onPickItems,
  initialSearchQuery,
  setIsModalOpen,
  setInvoiceData,
  setTransferInvNo,
  documentType,
}) => {
  const isPurchaseReturn = documentType === "PURCHASERETURN";
  const docNoField = isPurchaseReturn ? "SRVNO" : "INVNO";
  const dateField = isPurchaseReturn ? "PINV_DATE" : "INV_DATE";
  const customerField = isPurchaseReturn ? "SUPPLIER" : "CUSTOMER";

  const {
    searchTerm,
    setSearchTerm,
    pageSize,
    setPageSize,
    pageNumber,
    setPageNumber,
    rows,
    loading,
    totalCount,
    config,
  } = useDocumentList({
    docType: isPurchaseReturn ? "PINV" : "INV",
    docLabel: "Invoice",
    p1Value: "TOP",
    p2Value: "-",
    docNoField,
    screenCode: "INVCOUNT",
  });
  const containerRef = useRef(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [itemQuantities, setItemQuantities] = useState({});
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [checkedItems, setCheckedItems] = useState({});

  const getUniqueItemId = (invNo, item, index) => {
    const lineId = item.it_slno || index;
    return `${invNo}-${lineId}`;
  };
  // https://cubixweberp.com:213/api/CRMDocListView/SUNRISEWEB/PINV/FULL/-/-/-/-/MAIN/1/100


  // Fetch invoice details
  const fetchInvoiceDetails = async (invNo) => {
    if (invoiceDetails[invNo]) return;

    setLoadingDetails((prev) => ({ ...prev, [invNo]: true }));

    try {
      const detailUrl = isPurchaseReturn
        ? `${config.BASE_URL}/api/DocumentEditDetails/${config.COMPANY_CODE}/PINV/${invNo}/${config.DEPARTMENT_NO}`
        : `${config.BASE_URL}/api/SalesInvoiceDetail/${config.COMPANY_CODE}/${invNo}/${config.DEPARTMENT_NO}`;

      const response = await fetch(detailUrl);
      const data = await response.json();
      await new Promise((resolve) => setTimeout(resolve, 500));

      setInvoiceDetails((prev) => ({
        ...prev,
        [invNo]: data,
      }));

      const itemQtyField = isPurchaseReturn ? "tr_qty2" : "tr_qty";

      // Initialize quantities for items
      const initialQtys = {};
      const initialChecked = {};
      data.forEach((item, idx) => {
        const uniqueId = getUniqueItemId(invNo, item, idx);
        initialQtys[uniqueId] = item[itemQtyField] || 0;
        initialChecked[uniqueId] = false;
      });
      setItemQuantities((prev) => ({ ...prev, ...initialQtys }));
      setCheckedItems((prev) => ({ ...prev, ...initialChecked }));
    } catch (error) {
      console.error(`Error fetching details for ${invNo}:`, error);
      setInvoiceDetails((prev) => ({ ...prev, [invNo]: [] }));
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [invNo]: false }));
    }
  };

  const handleInvoiceClick = (invoice) => {
    const invNo = invoice[docNoField];

    setSelectedRowData(invoice);

    if (setInvoiceData)
      setInvoiceData((prev) => ({
        ...prev,
        inv_no: invNo,
      }));

    // setTransferInvNo(invNo);

    setDrawerOpen(true);
    setItemSearchTerm("");
    fetchInvoiceDetails(invNo);
  };

  // Handle individual checkbox toggle
  const handleCheckboxToggle = (uniqueItemId) => {
    setCheckedItems((prev) => ({
      ...prev,
      [uniqueItemId]: !prev[uniqueItemId],
    }));
  };

  // Handle select all checkbox
  const handleSelectAll = (checked) => {
    const updates = {};
    filteredDetails.forEach((item, index) => {
      const originalIndex = details.indexOf(item);
      const uniqueId = getUniqueItemId(
        selectedRowData[docNoField],
        item,
        originalIndex,
      );
      updates[uniqueId] = checked;
    });
    setCheckedItems((prev) => ({ ...prev, ...updates }));
  };

  // Check if all filtered items are selected
  const isAllSelected = () => {
    if (filteredDetails.length === 0) return false;
    return filteredDetails.every((item, index) => {
      const originalIndex = details.indexOf(item);
      const uniqueId = getUniqueItemId(
        selectedRowData[docNoField],
        item,
        originalIndex,
      );
      return checkedItems[uniqueId];
    });
  };

  // Check if some (but not all) filtered items are selected
  const isSomeSelected = () => {
    if (filteredDetails.length === 0) return false;
    const selectedCount = filteredDetails.filter((item, index) => {
      const originalIndex = details.indexOf(item);
      const uniqueId = getUniqueItemId(
        selectedRowData[docNoField],
        item,
        originalIndex,
      );
      return checkedItems[uniqueId];
    }).length;
    return selectedCount > 0 && selectedCount < filteredDetails.length;
  };

  // Handle quantity change
  const handleQuantityChange = (uniqueItemId, value) => {
    const numValue = parseFloat(value) || 0;
    setItemQuantities((prev) => ({
      ...prev,
      [uniqueItemId]: numValue,
    }));
  };

  // Calculate line total based on new quantity
  const calculateLineTotal = (unitPrice, newQty) => {
    return (unitPrice * newQty).toFixed(2);
  };

  // Filter items based on search term
  const filterItems = (items) => {
    if (!itemSearchTerm.trim()) return items;

    const searchLower = itemSearchTerm.toLowerCase().trim();
    return items.filter((item) => {
      const code = (item.code || "").toLowerCase();
      const desc = (item.idesc || "").toLowerCase();
      return code.includes(searchLower) || desc.includes(searchLower);
    });
  };
  const getCheckedItemsToTransfer = () => {
    if (!selectedRowData?.[docNoField]) return { itemsToTransfer: [], errors: [] };
    const invNo = selectedRowData[docNoField];
    const details = invoiceDetails[invNo] || [];

    const itemsToTransfer = [];
    const errors = [];

    const itemQtyField = isPurchaseReturn ? "tr_qty2" : "tr_qty";

    details.forEach((item, index) => {
      const uniqueId = getUniqueItemId(invNo, item, index);
      const isChecked = !!checkedItems[uniqueId];
      if (!isChecked) return;

      const newQty = Number(itemQuantities[uniqueId] ?? 0);
      const originalQty = Number(item[itemQtyField] ?? 0);

      if (isNaN(newQty) || newQty <= 0) {
        errors.push(
          `${item.code || "(unknown)"}: return quantity must be greater than 0`,
        );
        return;
      }

      if (newQty > originalQty) {
        errors.push(
          `${item.code || "(unknown)"
          }: return qty (${newQty}) exceeds invoice qty (${originalQty})`,
        );
        return;
      }

      itemsToTransfer.push({
        ...item,
        tr_qty: newQty,
        so_qty: newQty,
      });
    });

    return { itemsToTransfer, errors };
  };

  const hasAnyChecked = (() => {
    if (!selectedRowData?.[docNoField]) return false;
    const invNo = selectedRowData[docNoField];
    const details = invoiceDetails[invNo] || [];
    return details.some(
      (item, idx) => !!checkedItems[getUniqueItemId(invNo, item, idx)],
    );
  })();

  // Handle transfer with selected quantities
  const handleTransfer = () => {
    if (!selectedRowData?.[docNoField]) {
      alert("Please select an invoice to transfer.");
      return;
    }

    const { itemsToTransfer, errors } = getCheckedItemsToTransfer();

    if (errors.length > 0) {
      // show a concise error alert; can be improved to a modal / toast
      alert(
        "Please fix the following before transferring:\n\n" + errors.join("\n"),
      );
      return;
    }

    if (!itemsToTransfer || itemsToTransfer.length === 0) {
      alert("Please check and enter quantity for at least one item.");
      return;
    }
    setIsModalOpen(false);

    // If all validations pass, call parent handler
    if (onPickItems) {
      onPickItems(selectedRowData[docNoField], itemsToTransfer);
      console.log("itemsToTransfer",itemsToTransfer)
    }

    // Close drawer and reset selection
    setDrawerOpen(false);
    setSelectedRowData(null);
    setItemSearchTerm("");
  };

  // Close drawer
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedRowData(null);
    setItemSearchTerm("");
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Enter" && drawerOpen && selectedRowData?.[docNoField]) {
        e.preventDefault();
        handleTransfer();
      } else if (e.key === "Escape" && drawerOpen) {
        e.preventDefault();
        handleCloseDrawer();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen, selectedRowData, itemQuantities]);

  // Handle initial search query
  useEffect(() => {
    if (!initialSearchQuery) return;

    const q = String(initialSearchQuery).trim();
    if (!q) return;

    setSearchTerm(q);
    setPageNumber(1);

    const invoiceFromRows = rows.find((r) => String(r[docNoField]) === q);
    if (invoiceFromRows) {
      handleInvoiceClick(invoiceFromRows);
    }
  }, [initialSearchQuery]);

  const details = invoiceDetails[selectedRowData?.[docNoField]] || [];
  const isLoadingDetails = loadingDetails[selectedRowData?.[docNoField]];
  const filteredDetails = filterItems(details);

  // Count of checked items for the currently selected invoice
  const checkedCount = (() => {
    if (!selectedRowData?.[docNoField]) return 0;
    const invNo = selectedRowData[docNoField];
    return Object.keys(checkedItems).filter(
      (k) => k.startsWith(`${invNo}-`) && checkedItems[k],
    ).length;
  })();
  return (
    <>
      <div
        ref={containerRef}
        tabIndex={0}
        className="bg-white rounded-lg flex flex-col h-full"
        style={{ maxHeight: "85vh" }}
      >
        <div className="flex-shrink-0 px-4 py-2.5 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {/* Search Bar */}
            <div className="w-64 relative flex-shrink-0">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                autoFocus
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-2.5 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
              />
            </div>

            {selectedRowData ? (
              <div className="flex-1 bg-blue-50 border border-blue-300 rounded-md px-3 py-1.5 flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <span className="font-semibold text-blue-700 truncate">
                      {selectedRowData?.[docNoField]}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-600 truncate">
                      {selectedRowData?.[customerField] || "No customer"}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-md px-3 py-1.5 flex items-center gap-2 text-gray-500 text-sm">
                <Package className="w-4 h-4 flex-shrink-0" />
                <span>Select an invoice to view items</span>
              </div>
            )}
          </div>
        </div>

        {/* Pagination Top */}
        <div className="flex-shrink-0 bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            <span className="font-medium">
              {rows.length > 0 ? (pageNumber - 1) * pageSize + 1 : 0}
            </span>
            {" - "}
            <span className="font-medium">
              {Math.min(pageNumber * pageSize, totalCount)}
            </span>
            {" of "}
            <span className="font-medium">{totalCount}</span> invoices
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
              disabled={pageNumber === 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700 px-2">
              Page <span className="font-medium">{pageNumber}</span>
            </span>
            <button
              onClick={() => setPageNumber(pageNumber + 1)}
              disabled={pageNumber * pageSize >= totalCount}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              Next
            </button>
          </div>
        </div>

        {/* Invoice List */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full py-12">
              <Loader2 className="w-7 h-7 animate-spin text-blue-600 mb-2" />
              <p className="text-sm text-gray-600">Loading invoices...</p>
            </div>
          ) : rows && rows.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {rows.map((invoice) => {
                const isSelected = selectedRowData?.[docNoField] === invoice[docNoField];

                return (
                  <div
                    key={invoice[docNoField]}
                    className={`transition-colors cursor-pointer ${isSelected ? "bg-blue-50/50" : "hover:bg-gray-50"
                      }`}
                    onClick={() => handleInvoiceClick(invoice)}
                  >
                    <div
                      className={`flex items-center px-4 py-3 ${isSelected ? "border-l-4 border-blue-600" : ""
                        }`}
                    >
                      <div className="flex-1 flex items-center gap-4 text-sm">
                        <div className="w-32">
                          <span className="font-semibold text-gray-900">
                            {invoice[docNoField]}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-gray-700 truncate block">
                            {invoice[customerField] || "-"}
                          </span>
                        </div>
                        <div className="w-24 text-gray-600">
                          {invoice[dateField]
                            ? new Date(invoice[dateField]).toLocaleDateString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "2-digit",
                              },
                            )
                            : "-"}
                        </div>
                        <div className="w-24 text-right font-medium text-gray-900">
                          {invoice.AMOUNT || "-"}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 ml-2" />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-12 text-gray-500">
              <Search className="w-10 h-10 mb-2 opacity-40" />
              <p className="text-sm">No invoices found</p>
              <p className="text-xs text-gray-400 mt-1">
                Try adjusting your search
              </p>
            </div>
          )}
        </div>
      </div>

      <ItemListDrawerSalesReturn
        invoiceDetails={invoiceDetails}
        selectedRowData={selectedRowData}
        filteredDetails={filteredDetails}
        loadingDetails={loadingDetails}
        filterItems={filterItems}
        drawerOpen={drawerOpen}
        handleCloseDrawer={handleCloseDrawer}
        itemSearchTerm={itemSearchTerm}
        setItemSearchTerm={setItemSearchTerm}
        getUniqueItemId={getUniqueItemId}
        itemQuantities={itemQuantities}
        handleQuantityChange={handleQuantityChange}
        calculateLineTotal={calculateLineTotal}
        handleTransfer={handleTransfer}
        isAllSelected={isAllSelected}
        isSomeSelected={isSomeSelected}
        handleSelectAll={handleSelectAll}
        checkedItems={checkedItems}
        hasAnyChecked={hasAnyChecked}
        checkedCount={checkedCount}
        handleCheckboxToggle={handleCheckboxToggle}
        docNoField={docNoField}
        customerField={customerField}
        isPurchaseReturn={isPurchaseReturn}
      />
    </>
  );
};

export default TransferInvoiceListModal;
